/**
 * A reference to the PrimeReact Toast component.
 *
 * This will be set from a component that renders `<Toast ref={...} />`
 * by calling `setToastRef` once during initialization.
 * @author niyas.shajhan
 */
var toastRef = null;
/**
 * Sets the reference for the global toast instance.
 *
 * This should be called in the component where the `<Toast>` is defined,
 * typically inside a `useEffect` or during render:
 *
 * ```tsx
 * const myToastRef = useRef<Toast>(null);
 * useEffect(() => {
 *   setToastRef(myToastRef);
 * }, []);
 *
 * return <Toast ref={myToastRef} />;
 * ```
 *
 * @param ref - A `RefObject<Toast>` obtained from `useRef`.
 */
export var setToastRef = function (ref) {
    toastRef = ref;
};
/**
 * Shows a toast notification using the globally stored Toast reference.
 *
 * Make sure to call `setToastRef` first to provide a valid Toast instance.
 *
 * Example:
 * ```ts
 * showToast({ severity: 'success', summary: 'Saved', detail: 'Data saved successfully' });
 * ```
 *
 * You can also pass an array of `ToastMessage` objects to show multiple messages:
 * ```ts
 * showToast([
 *   { severity: 'info', summary: 'Step 1', detail: 'Started' },
 *   { severity: 'success', summary: 'Step 2', detail: 'Completed' },
 * ]);
 * ```
 *
 * @param options - A `ToastMessage` object or an array of `ToastMessage` objects.
 */
export var showToast = function (options) {
    var _a;
    (_a = toastRef === null || toastRef === void 0 ? void 0 : toastRef.current) === null || _a === void 0 ? void 0 : _a.show(options);
};
